/**
 * +field: open-field mode (docs/design/zones.md Phase 1) for maps with `DungeonDef.field`.
 *
 *   - Spawn points keep `count` mobs alive. A dead mob's slot respawns `respawnMs` after the death
 *     (a [min, max] range rolls seeded jitter, so a cleared camp trickles back) with a fresh family
 *     member and level (seeded), scattered within the spawn radius, scaled by levelScaling(level)
 *     only. Every (re)spawn emits 'field_spawn'.
 *   - +zone (FieldDef.respawn): a slot due while a living hero stands within `blockYd` of its spawn
 *     point waits (at most `blockMaxMs`), so nothing pops in on top of a player; kills at a point in
 *     the last `quicken.windowMs` shorten its next delay down to `quicken.floorMs` (grinding a spot
 *     never dries it up completely).
 *   - Dead NPCs without a pack (field mobs, their summons) are removed from the world
 *     FIELD_CORPSE_MS after death, so a long session doesn't grow `unitOrder`.
 *   - Aggro / call for help / evade / leash / wander are the AI's (field mobs carry `spawnId`; see
 *     field/defs.ts and ai/index.ts).
 *   - Player units stepping into a portal emit 'portal' once per entry (edge trigger).
 *   - Dead heroes respawn at the arrival point FIELD_RELEASE_MS after death with full HP (or at once
 *     on interact 'releaseSpirit'); mobs that were on them have already evaded (empty threat).
 *   - Heroes out of combat for FIELD_REGEN_DELAY_MS regenerate HP and mana quickly.
 *   - FieldDef.partyAuras are applied by role at spawn and after every respawn.
 *   - +zone (FieldDef.dormantYd): spawn points far from every hero park their mobs (out of the
 *     world, slots due at once) and fill again when a hero nears, so a big zone simulates only the
 *     neighbourhood around the party. Timers of dead slots keep running while parked.
 *   - +zone: interact 'waystone:<id>' at a waystone moves the hero to that stone (useFieldWaystone).
 * The M+ system is inert on field maps. All state lives in `world.field`; randomness is ctx.rng only.
 */
import type { DungeonDef, FieldDef, FieldSpawnDef, PortalDef, Radians, SimContext, Unit, UnitId, Vec2 } from '../contracts';
import { FIELD_CORPSE_MS, FIELD_REGEN_DELAY_MS, FIELD_REGEN_PCT_PER_SEC, FIELD_RELEASE_MS, TICK_MS, WAYSTONE_COOLDOWN_MS, WAYSTONE_USE_YD } from '../contracts';
import { levelScaling } from './level';
import { initFieldLoot, tickFieldLoot } from './loot'; // +field: ground loot
import { tickGather } from './gather'; // +prof

export { fieldAreaAt, fieldLevelAt, fieldPackDef, fieldRegionAt, fieldSpawnDef } from './defs';

/** Corpse sweep interval (a dead mob lingers FIELD_CORPSE_MS .. + this). */
const SWEEP_MS = 1000;
/** Tries to find a walkable scatter point before falling back to the spawn centre. */
const SCATTER_TRIES = 6;
/** Hero respawn offsets around the arrival point (same layout as the party spawn). */
const FORMATION: Vec2[] = [
  { x: 2, y: 0 },
  { x: -2, y: 0 },
  { x: 0, y: 2 },
  { x: 0, y: -2 },
];

const fieldOf = (ctx: SimContext): FieldDef | undefined => ctx.content.dungeon(ctx.world.dungeonId)?.field;

/** Where the party arrives (and heroes respawn): SimConfig.arrive, else the map entrance. */
export function arrivalPoint(dungeon: DungeonDef, arrive?: { pos: Vec2; facing: Radians }): { pos: Vec2; facing: Radians } {
  const a = arrive ?? { pos: dungeon.entrance, facing: dungeon.entranceFacing };
  return { pos: { x: a.pos.x, y: a.pos.y }, facing: a.facing };
}

/** Party slot offset around a point (walkable). */
export function formationPoint(ctx: SimContext, at: Vec2, slot: number): Vec2 {
  const off = FORMATION[slot % FORMATION.length]!;
  return ctx.sys.spatial.clampToWalkable(ctx.world, { x: at.x + off.x, y: at.y + off.y });
}

/** The portal containing `p` (first in definition order), if any. */
export function portalAt(field: FieldDef, p: Vec2): PortalDef | undefined {
  for (const portal of field.portals) {
    const dx = p.x - portal.pos.x;
    const dy = p.y - portal.pos.y;
    if (dx * dx + dy * dy <= portal.radius * portal.radius) return portal;
  }
  return undefined;
}

/**
 * Set up world.field and fill every spawn point. Call after the party is spawned (arrival point
 * already applied) and the spatial system is initialised.
 */
export function initField(ctx: SimContext, dungeon: DungeonDef, arrive?: { pos: Vec2; facing: Radians }): void {
  const field = dungeon.field;
  if (!field) return;
  const w = ctx.world;
  w.field = { spawns: {}, kills: 0, arrive: arrivalPoint(dungeon, arrive), portalInside: {}, lastCombatAt: {}, nextSweepAt: ctx.now + SWEEP_MS };
  const loot = initFieldLoot(ctx, field);
  if (loot) w.field.loot = loot;
  for (const s of field.spawns) {
    const count = Math.max(0, Math.floor(s.count ?? 1));
    w.field.spawns[s.id] = { unitIds: new Array<UnitId | null>(count).fill(null), respawnAt: new Array<number | null>(count).fill(null) };
    // +zone: points far from the arriving party start parked (due at once, filled when a hero nears).
    if (field.dormantYd && !heroNear(ctx, s.pos, field.dormantYd * WAKE_K + s.radius)) {
      w.field.spawns[s.id]!.dormant = true;
      w.field.spawns[s.id]!.respawnAt.fill(ctx.now);
      continue;
    }
    for (let slot = 0; slot < count; slot++) spawnSlot(ctx, s, slot);
  }
  for (const id of w.partyIds) {
    const u = w.units[id];
    if (!u) continue;
    // Arriving inside a portal mouth doesn't send you straight back: only a fresh entry fires.
    w.field.portalInside![id] = portalAt(field, u.pos)?.id ?? null;
    applyPartyAuras(ctx, u, field);
  }
}

/** Per tick (after deaths): kills + respawn timers, respawns, corpse sweep, heroes (release, regen, portals). */
export function tickField(ctx: SimContext): void {
  const w = ctx.world;
  const st = w.field;
  const field = fieldOf(ctx);
  if (!st || !field) return;

  // Deaths this tick (death events are only emitted by combat.resolveDeaths, earlier this tick).
  for (const e of w.events) {
    if (e.type !== 'death') continue;
    const u = w.units[e.targetID];
    if (!u || u.kind !== 'npc' || u.faction !== 'enemy') continue;
    st.kills += 1;
    if (!u.spawnId) continue;
    const ss = st.spawns[u.spawnId];
    const slot = ss ? ss.unitIds.indexOf(u.id) : -1;
    const def = field.spawns.find((s) => s.id === u.spawnId);
    if (ss && slot >= 0 && def) ss.respawnAt[slot] = ctx.now + respawnDelay(ctx, field, def, ss);
  }

  // Respawns (definition order, then slot order: deterministic).
  const rules = field.respawn;
  for (const def of field.spawns) {
    const ss = st.spawns[def.id];
    if (!ss || ss.dormant) continue;
    for (let slot = 0; slot < ss.respawnAt.length; slot++) {
      const at = ss.respawnAt[slot];
      // The old corpse (if still lying there) is left to the sweep; the slot takes the new mob.
      if (at === null || at === undefined || ctx.now < at) continue;
      if (rules?.blockYd && heroNear(ctx, def.pos, rules.blockYd + def.radius)) {
        const due = ((ss.dueAt ??= new Array<number | null>(ss.respawnAt.length).fill(null))[slot] ??= at);
        if (ctx.now - due < (rules.blockMaxMs ?? 120_000)) {
          ss.respawnAt[slot] = ctx.now + 1000;
          continue;
        }
      }
      if (ss.dueAt) ss.dueAt[slot] = null;
      spawnSlot(ctx, def, slot);
    }
  }

  tickFieldLoot(ctx, field);
  tickGather(ctx); // +prof

  if (ctx.now >= (st.nextSweepAt ?? 0)) {
    st.nextSweepAt = ctx.now + SWEEP_MS;
    sweepCorpses(ctx);
    if (field.dormantYd) tickDormancy(ctx, field, field.dormantYd);
  }

  tickHeroes(ctx, field);
}

/** Interact 'releaseSpirit' on a field map: respawn now at the arrival point. */
export function releaseFieldHero(ctx: SimContext, unitId: UnitId): void {
  const field = fieldOf(ctx);
  const u = ctx.world.units[unitId];
  if (!field || !u || u.alive || u.departed || u.kind !== 'player') return;
  respawnHero(ctx, u, field);
}

/**
 * +zone: interact 'waystone:<id>'. The hero must be alive, out of combat, off cooldown and within
 * WAYSTONE_USE_YD of a waystone; arrives beside the named one. Returns whether it travelled.
 */
export function useFieldWaystone(ctx: SimContext, unitId: UnitId, toId: string): boolean {
  const field = fieldOf(ctx);
  const st = ctx.world.field;
  const u = ctx.world.units[unitId];
  const stones = field?.waystones;
  if (!field || !st || !stones || !u || !u.alive || u.departed || u.kind !== 'player' || u.inCombat) return false;
  if (u.jump || u.perch) return false; // +glide: feet on the ground to travel
  const to = stones.find((s) => s.id === toId);
  if (!to) return false;
  if (!stones.some((s) => Math.hypot(s.pos.x - u.pos.x, s.pos.y - u.pos.y) <= WAYSTONE_USE_YD)) return false;
  const ready = (st.waystoneReadyAt ??= {});
  if (ctx.now < (ready[unitId] ?? 0)) return false;
  ready[unitId] = ctx.now + WAYSTONE_COOLDOWN_MS;
  const from = { ...u.pos };
  // Arrive a couple of yards south of the stone (it is an obstacle-free prop), facing it.
  const pos = { x: to.pos.x, y: to.pos.y - 2.5 };
  if (u.cast) ctx.sys.casting.cancelCast(ctx, u.id, 'moved');
  u.pos = { ...pos };
  u.move = null;
  u.targetId = null;
  u.facing = Math.atan2(to.pos.y - pos.y, to.pos.x - pos.x);
  (st.portalInside ??= {})[u.id] = portalAt(field, pos)?.id ?? null;
  ctx.emit({ type: 'waystone', sourceID: u.id, waystoneId: to.id, from, to: { ...pos } });
  // Fill the arrival's neighbourhood now (not a second later).
  if (field.dormantYd) tickDormancy(ctx, field, field.dormantYd);
  return true;
}

/** +zone: a dormant point wakes when a hero is within this fraction of dormantYd (hysteresis). */
const WAKE_K = 0.85;

/** +zone: park far spawn points, wake near ones (FieldDef.dormantYd); once a second. */
function tickDormancy(ctx: SimContext, field: FieldDef, yd: number): void {
  const w = ctx.world;
  const st = w.field!;
  const heroes: Vec2[] = [];
  for (const id of w.partyIds) {
    const h = w.units[id];
    if (h && !h.departed) heroes.push(h.pos);
  }
  const near = (p: Vec2, r: number): boolean => heroes.some((h) => Math.abs(h.x - p.x) < r && Math.abs(h.y - p.y) < r && Math.hypot(h.x - p.x, h.y - p.y) < r);
  let gone: Set<UnitId> | null = null;
  for (const def of field.spawns) {
    const ss = st.spawns[def.id];
    if (!ss) continue;
    if (ss.dormant) {
      if (!near(def.pos, yd * WAKE_K + def.radius)) continue;
      ss.dormant = false;
      // Due slots fill through the normal respawn path (next tick), honouring blockYd.
      continue;
    }
    if (near(def.pos, yd + def.radius)) continue;
    // Any of its mobs busy (in combat, hurt, off home, near a hero) keeps the point live.
    let busy = false;
    for (const uid of ss.unitIds) {
      const m = uid ? w.units[uid] : undefined;
      if (m?.alive && (m.inCombat || m.hp < m.maxHp || near(m.pos, yd))) busy = true;
    }
    if (busy) continue;
    ss.dormant = true;
    ss.unitIds.forEach((uid, slot) => {
      const m = uid ? w.units[uid] : undefined;
      if (!m) return;
      if (m.alive) {
        (gone ??= new Set()).add(m.id);
        ss.respawnAt[slot] = ctx.now;
      }
    });
  }
  if (gone) removeUnits(ctx, gone);
}

// ---------------------------------------------------------------------------

/** Roll a slot's respawn delay: the spawn's (else the map's) delay or range, quickened by recent kills here. */
export function respawnDelay(ctx: SimContext, field: FieldDef, def: FieldSpawnDef, ss: { kills?: number[] }): number {
  const r = def.respawnMs ?? field.respawnMs;
  let ms = typeof r === 'number' ? r : r[1] > r[0] ? ctx.rng.int(r[0], r[1]) : r[0];
  const q = field.respawn?.quicken;
  if (q) {
    const kills = (ss.kills ??= []);
    while (kills.length && ctx.now - kills[0]! > q.windowMs) kills.shift();
    kills.push(ctx.now);
    // The first kill in a while is the normal pace; each further one shaves a little off.
    if (ms > q.floorMs) ms = Math.max(q.floorMs, Math.round(ms * (1 - q.perKill * (kills.length - 1))));
  }
  return ms;
}

/** A living hero within `r` yards of `p`. */
function heroNear(ctx: SimContext, p: Vec2, r: number): boolean {
  const w = ctx.world;
  for (const id of w.partyIds) {
    const h = w.units[id];
    if (h?.alive && !h.departed && Math.hypot(h.pos.x - p.x, h.pos.y - p.y) < r) return true;
  }
  return false;
}

function spawnSlot(ctx: SimContext, def: FieldSpawnDef, slot: number): void {
  const w = ctx.world;
  const ss = w.field!.spawns[def.id]!;
  ss.respawnAt[slot] = null;
  ss.unitIds[slot] = null;
  const npcId = def.npcIds.length <= 1 ? def.npcIds[0] : ctx.rng.pick(def.npcIds);
  if (!npcId || !ctx.content.npc(npcId)) return;
  const lo = Math.min(def.level[0], def.level[1]);
  const hi = Math.max(def.level[0], def.level[1]);
  const level = Math.max(1, lo === hi ? lo : ctx.rng.int(lo, hi));
  const pos = scatter(ctx, def);
  const facing = def.facing ?? ctx.rng.range(-Math.PI, Math.PI); // +specs: fixed-facing spawns roll nothing
  const u = ctx.ops.spawnNpc({ npcId, pos, facing, packId: null });
  const sc = levelScaling(level);
  u.scaling = { ...sc };
  u.maxHp = Math.max(1, Math.round(ctx.content.npcBaseHp(npcId) * sc.hp));
  u.hp = u.maxHp;
  u.level = level;
  if (def.elite) u.elite = true;
  u.spawnId = def.id;
  ss.unitIds[slot] = u.id;
  ctx.emit({ type: 'field_spawn', sourceID: u.id, spawnId: def.id, level });
}

/** Uniform point in the spawn disc that is walkable (else the walkable point nearest the centre). */
function scatter(ctx: SimContext, def: FieldSpawnDef): Vec2 {
  const sp = ctx.sys.spatial;
  for (let i = 0; i < SCATTER_TRIES && def.radius > 0; i++) {
    const a = ctx.rng.range(0, Math.PI * 2);
    const r = def.radius * Math.sqrt(ctx.rng.next());
    const p = { x: def.pos.x + Math.cos(a) * r, y: def.pos.y + Math.sin(a) * r };
    if (sp.isWalkable(ctx.world, p)) return p;
  }
  return sp.clampToWalkable(ctx.world, { x: def.pos.x, y: def.pos.y });
}

/** Remove dead pack-less NPCs whose corpse has lingered FIELD_CORPSE_MS (one pass over unitOrder). */
function sweepCorpses(ctx: SimContext): void {
  const w = ctx.world;
  let gone: Set<UnitId> | null = null;
  for (const id of w.unitOrder) {
    const u = w.units[id];
    if (!u || u.kind !== 'npc' || u.alive || u.packId || u.diedAt === null || ctx.now - u.diedAt < FIELD_CORPSE_MS) continue;
    (gone ??= new Set()).add(id);
  }
  if (!gone) return;
  // A corpse still feeding a DoT, zone or telegraph stays until those end (they read its scaling).
  for (const id of w.unitOrder) for (const a of w.units[id]?.auras ?? []) gone.delete(a.casterId);
  for (const z of w.zones) gone.delete(z.sourceId);
  for (const t of w.telegraphs) gone.delete(t.sourceId);
  if (gone.size > 0) removeUnits(ctx, gone);
}

/** Delete units (one unitOrder pass) and clear their spawn slots (the respawn timer keeps running). */
function removeUnits(ctx: SimContext, ids: Set<UnitId>): void {
  const w = ctx.world;
  for (const id of ids) {
    const u = w.units[id];
    if (u?.spawnId) {
      const ss = w.field?.spawns[u.spawnId];
      const slot = ss ? ss.unitIds.indexOf(id) : -1;
      if (ss && slot >= 0) ss.unitIds[slot] = null;
    }
    delete w.units[id];
  }
  w.unitOrder = w.unitOrder.filter((id) => !ids.has(id));
  for (const id of w.unitOrder) {
    const u = w.units[id];
    if (u?.targetId && ids.has(u.targetId)) u.targetId = null;
  }
}

function tickHeroes(ctx: SimContext, field: FieldDef): void {
  const w = ctx.world;
  const st = w.field!;
  const inside = (st.portalInside ??= {});
  const lastCombat = (st.lastCombatAt ??= {});
  const dt = TICK_MS / 1000;
  for (const id of w.partyIds) {
    const u = w.units[id];
    if (!u || u.departed || u.kind !== 'player') continue;
    if (!u.alive) {
      if (u.diedAt !== null && ctx.now >= u.diedAt + FIELD_RELEASE_MS) respawnHero(ctx, u, field);
      continue;
    }
    // Out-of-combat regen (WoW eating/drinking without the food).
    if (u.inCombat) lastCombat[id] = ctx.now;
    else if (ctx.now - (lastCombat[id] ?? -Infinity) >= FIELD_REGEN_DELAY_MS) regen(ctx, u, dt);
    // Portals: edge trigger on entry.
    const portal = portalAt(field, u.pos);
    const was = inside[id] ?? null;
    if (portal && portal.id !== was) {
      ctx.emit({ type: 'portal', sourceID: id, portalId: portal.id, to: portal.to, ...(portal.toPortal ? { toPortal: portal.toPortal } : {}) });
    }
    inside[id] = portal?.id ?? null;
  }
}

function regen(ctx: SimContext, u: Unit, dt: number): void {
  if (u.hp < u.maxHp) u.hp = Math.min(u.maxHp, u.hp + u.maxHp * FIELD_REGEN_PCT_PER_SEC * dt);
  const cls = u.classId ? ctx.content.class(u.classId) : undefined;
  for (const r of cls?.resources ?? []) {
    // Only pools that start full (mana, energy) refill; rage-like pools keep their own decay.
    if (r.start < r.max) continue;
    const pool = u.resources[r.id];
    if (pool && pool.cur < pool.max) pool.cur = Math.min(pool.max, pool.cur + pool.max * FIELD_REGEN_PCT_PER_SEC * dt);
  }
}

function respawnHero(ctx: SimContext, u: Unit, field: FieldDef): void {
  const w = ctx.world;
  const st = w.field!;
  const a = st.arrive ?? { pos: { x: 0, y: 0 }, facing: 0 };
  const pos = formationPoint(ctx, a.pos, Math.max(0, w.partyIds.indexOf(u.id)));
  ctx.sys.combat.revive(ctx, u.id, 1, pos);
  u.pos = pos;
  u.hp = u.maxHp;
  u.facing = a.facing;
  u.targetId = null;
  u.move = null;
  applyPartyAuras(ctx, u, field);
  (st.portalInside ??= {})[u.id] = portalAt(field, pos)?.id ?? null;
}

/** Class passives (unless they survived death) and the field's role aura. */
function applyPartyAuras(ctx: SimContext, u: Unit, field: FieldDef): void {
  const has = (id: string) => u.auras.some((a) => a.auraId === id);
  const cls = u.classId ? ctx.content.class(u.classId) : undefined;
  for (const aura of cls?.passives ?? []) if (!has(aura.id)) ctx.sys.combat.applyAura(ctx, u.id, aura.id, u.id, aura.id);
  const roleAura = u.role ? field.partyAuras?.[u.role] : undefined;
  if (roleAura && ctx.content.aura(roleAura) && !has(roleAura)) ctx.sys.combat.applyAura(ctx, u.id, roleAura, u.id, roleAura);
}
